/**
 * useGuardShift Hook
 *
 * React Query hook for fetching a guard's current shift patrols.
 * Used by the Guard Summary modal to display the guard's shift overview.
 *
 * Features:
 * - Only fetches when guardId is provided and modal is open
 * - 30-second stale time to cache shift data briefly
 * - Returns patrol list with status and checkpoint progress
 */

import { useQuery } from '@tanstack/react-query';
import { fetchGuardCurrentShift } from '../../../../../api/dispatch';
import { GuardCurrentShiftResponse, ShiftPatrol } from '../../../../../api/dispatch/types';

/** Query key for guard shift */
export const GUARD_SHIFT_QUERY_KEY = ['guardShift'];

/** Stale time (30 seconds) */
const STALE_TIME = 30 * 1000;

export interface UseGuardShiftOptions {
  /** The guard's contact ID to fetch shift for */
  guardId: number | null;
  /** Whether the modal is open (controls enabled state) */
  enabled?: boolean;
}

export interface UseGuardShiftResult {
  /** Guard's name */
  guardName: string | null;
  /** Shift start time (ISO string) */
  shiftStart: string | null;
  /** Shift end time (ISO string, null if ongoing) */
  shiftEnd: string | null;
  /** Patrols in the current shift */
  patrols: ShiftPatrol[];
  /** Total patrols in shift */
  totalPatrols: number;
  /** Completed patrols count */
  completedPatrols: number;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Hook to fetch a guard's current shift patrols
 *
 * @param options - Configuration options
 * @returns Guard shift data with loading/error states
 */
export function useGuardShift(options: UseGuardShiftOptions): UseGuardShiftResult {
  const { guardId, enabled = true } = options;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery<GuardCurrentShiftResponse>({
    queryKey: [...GUARD_SHIFT_QUERY_KEY, guardId],
    queryFn: () => {
      if (!guardId) {
        throw new Error('guardId is required');
      }
      return fetchGuardCurrentShift(guardId);
    },
    enabled: enabled && guardId !== null,
    staleTime: STALE_TIME,
    // Don't refetch on window focus - user may have modal open
    refetchOnWindowFocus: false,
  });

  return {
    guardName: data?.guardName ?? null,
    shiftStart: data?.shiftStart ?? null,
    shiftEnd: data?.shiftEnd ?? null,
    patrols: data?.patrols ?? [],
    totalPatrols: data?.totalPatrols ?? 0,
    completedPatrols: data?.completedPatrols ?? 0,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    refetch,
  };
}

export default useGuardShift;
